import { NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { GlobalModule } from '../global/global.module';
import { SharedModule } from './components/shared/shared.module';
import { RequestHistoryModule } from './components/request-info-dialog/request-history/request-history.module';
import { RequestSubtasksModule } from './components/request-info-dialog/request-details/request-subtasks/request-subtasks.module';
import { SkeletonLoaderModule } from '../global/components/skeleton-loader/skeleton-loader.module';

import { CreateRequestPageComponent } from './components/create-request-page/create-request-page.component';
import { CreateRequestDialogComponent } from './components/create-request-dialog/create-request-dialog.component';
import { CategorySelectionComponent } from './components/create-request-dialog/category-selection/category-selection.component';
import { SubcategorySelectionComponent } from './components/create-request-dialog/subcategory-selection/subcategory-selection.component';
import { LocationSelectionComponent } from './components/create-request-dialog/location-selection/location-selection.component';
import { RequestSettingsDialogModule } from './components/request-settings-dialog/request-settings-dialog.module';
import { SettingsHeaderModule } from './components/request-settings-dialog/components/settings-header/settings-header.module';
import { SettingsFooterModule } from './components/request-settings-dialog/components/settings-footer/settings-footer.module';
import { CategoryListModule } from './components/request-settings-dialog/components/category-list/category-list.module';
import { CategoryFormModule } from './components/request-settings-dialog/components/category-form/category-form.module';
import { DepartmentListModule } from './components/request-settings-dialog/components/department-list/department-list.module';
import { DepartmentFormModule } from './components/request-settings-dialog/components/department-form/department-form.module';
import { StatusListModule } from './components/request-settings-dialog/components/status-list/status-list.module';
import { StatusFormModule } from './components/request-settings-dialog/components/status-form/status-form.module';
import { OtherSettingsModule } from './components/request-settings-dialog/components/other-settings/other-settings.module';
import { RequestListPageComponent } from './components/request-list-page/request-list-page.component';
import { ExportMenuComponent } from './components/export-menu/export-menu.component';
import { FilterMenuComponent } from './components/filter-menu/filter-menu.component';
import { RequestDetailsComponent } from './components/request-info-dialog/request-details/request-details.component';
import { RequestInfoComponent } from './components/request-info-dialog/request-details/request-info/request-info.component';
import { RequestDescriptionComponent } from './components/request-info-dialog/request-details/request-description/request-description.component';
import { RequestContactComponent } from './components/request-info-dialog/request-contact/request-contact.component';
import { RequestCategoryService } from './services/request-category.service';
import { RequestDepartmentService } from './services/request-department.service';
import { RequestsV2ImplService } from './services/requests-v2.impl.service';

// Leaflet CSS is now loaded via angular.json
import { StatusSelectComponent } from './components/shared/status-select/status-select.component';
import { DepartmentSelectComponent } from './components/shared/department-select/department-select.component';
import { RequestStatusService } from './services/request-status.service';

import { RequestInformationComponent } from './components/create-request-dialog/request-information/request-information.component';
import { CreateRequestLinksComponent } from './components/create-request-dialog/request-information/create-request-links/create-request-links.component';
import { RequestSentComponent } from './components/create-request-dialog/request-sent/request-sent.component';
import { ValidateInformationComponent } from './components/create-request-dialog/validate-information/validate-information.component';
import { FileAttachmentModule } from '../global/components/file-attachment/file-attachment.module';
import { SubjectSelectComponent } from './components/shared/subject-select/subject-select.component';
import { CategorySelectComponent } from './components/shared/category-select/category-select.component';
import { RequestInternalNoteComponent } from './components/request-info-dialog/request-details/request-internal-note/request-internal-note.component';
import { RequestInternalFilesComponent } from './components/request-info-dialog/request-details/request-internal-files/request-internal-files.component';
import { RequestFilterSidenavComponent } from './components/request-filter-sidenav/request-filter-sidenav.component';
import { RequestLinksComponent } from './components/request-info-dialog/request-details/request-links/request-links.component';
import { RequestMessagesComponent } from './components/request-info-dialog/request-messages/request-messages.component';
import { MentionPipe } from './pipes/mention.pipe';
import { RequestDashboardComponent } from './components/request-dashboard/request-dashboard.component';
import { RequestAnalyticsComponent } from './components/request-analytics/request-analytics.component';
import { RequestConfigComponent } from './components/request-config/request-config.component';
import { NgChartsModule } from 'ng2-charts';
import { AnalyticsKpiCardsComponent } from './components/request-analytics/analytics-kpi-cards/analytics-kpi-cards.component';
import { AnalyticsStatusChartComponent } from './components/request-analytics/analytics-status-chart/analytics-status-chart.component';
import { AnalyticsDepartmentChartComponent } from './components/request-analytics/analytics-department-chart/analytics-department-chart.component';
import { AnalyticsTrendChartComponent } from './components/request-analytics/analytics-trend-chart/analytics-trend-chart.component';
import { AnalyticsCategoryChartComponent } from './components/request-analytics/analytics-category-chart/analytics-category-chart.component';
import { RequestAnalyticsService } from './services/request-analytics.service';
import { AnalyticsHeatmapComponent } from './components/request-analytics/analytics-heatmap/analytics-heatmap.component';
import { AnalyticsPerformanceTableComponent } from './components/request-analytics/analytics-performance-table/analytics-performance-table.component';
import { SectionBlockComponent } from './components/section-block/section-block.component';
import { RequestInfosPanelComponent } from './components/request-infos-panel/request-infos-panel.component';
import { RequestInternalPanelComponent } from './components/request-internal-panel/request-internal-panel.component';
import { RequestDetailPageComponent } from './components/request-detail-page/request-detail-page.component';
import { LinkedRequestsStatusDialogComponent } from './components/linked-requests-status-dialog/linked-requests-status-dialog.component';
import { RequestPrintDialogComponent } from './components/request-print-dialog/request-print-dialog.component';

@NgModule({
    declarations: [
        CreateRequestPageComponent,
        CreateRequestDialogComponent,
        RequestListPageComponent,
        CategorySelectionComponent,
        SubcategorySelectionComponent,
        LocationSelectionComponent,
        ExportMenuComponent,
        FilterMenuComponent,
        RequestDetailsComponent,
        RequestInfoComponent,
        RequestLinksComponent,
        RequestDescriptionComponent,
        RequestInternalFilesComponent,
        RequestInternalNoteComponent,
        RequestContactComponent,
        StatusSelectComponent,
        DepartmentSelectComponent,
        RequestInformationComponent,
        CreateRequestLinksComponent,
        RequestSentComponent,
        ValidateInformationComponent,
        SubjectSelectComponent,
        CategorySelectComponent,
        RequestFilterSidenavComponent,
        RequestMessagesComponent,
        MentionPipe,
        RequestDashboardComponent,
        RequestAnalyticsComponent,
        RequestConfigComponent,
        AnalyticsKpiCardsComponent,
        AnalyticsStatusChartComponent,
        AnalyticsDepartmentChartComponent,
        AnalyticsTrendChartComponent,
        AnalyticsCategoryChartComponent,
        AnalyticsHeatmapComponent,
        AnalyticsPerformanceTableComponent,
        SectionBlockComponent,
        RequestInfosPanelComponent,
        RequestInternalPanelComponent,
        RequestDetailPageComponent,
        LinkedRequestsStatusDialogComponent,
        RequestPrintDialogComponent,
    ],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
        TranslateModule,
        RouterModule,
        HttpClientModule,
        MatDialogModule,
        MatInputModule,
        MatFormFieldModule,
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatDividerModule,
        MatSelectModule,
        MatListModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonToggleModule,
        MatCheckboxModule,
        MatAutocompleteModule,
        GlobalModule,
        RequestHistoryModule,
        SharedModule,
        RequestSettingsDialogModule,
        SettingsHeaderModule,
        SettingsFooterModule,
        CategoryListModule,
        CategoryFormModule,
        DepartmentListModule,
        DepartmentFormModule,
        StatusListModule,
        StatusFormModule,
        OtherSettingsModule,
        RequestSubtasksModule,
        FileAttachmentModule,
        SkeletonLoaderModule,
        MatPaginatorModule,
        MatSidenavModule,
        MatChipsModule,
        MatRadioModule,
        MatTooltipModule,
        ScrollingModule,
        MatTabsModule,
        NgChartsModule,
        ScrollingModule,
    ],
    exports: [
        CreateRequestPageComponent,
        RequestListPageComponent,
        RequestDashboardComponent,
        CreateRequestDialogComponent,
        ExportMenuComponent,
        FilterMenuComponent,
        SubjectSelectComponent,
        CategorySelectComponent,
        RequestFilterSidenavComponent,
    ],
    providers: [DatePipe, RequestCategoryService, RequestDepartmentService, RequestsV2ImplService, RequestStatusService, RequestAnalyticsService],
})
export class RequestV2Module {}
